import { useState, useEffect, useCallback } from "react";
import { BigNumber, providers } from "ethers";
import { chainbridgeConfig, BridgeConfig } from "../../chainbridgeConfig";
import { ERC721__factory as ERC721Factory } from "../../Contracts/factories/ERC721__factory";
import { ERC1155__factory as ERC1155Factory } from "../../Contracts/factories/ERC1155__factory";
import type { ERC721 } from "../../Contracts/ERC721.d";
import type { ERC1155 } from "../../Contracts/ERC1155.d";
import { formatGatewayUri } from "../../Utils/IpfsUriFormatter";
import type { NFTTokenDeclaration, ERC721Metadata } from "../types";

interface UseNFTTokensArgs {
  signer: providers.JsonRpcSigner | undefined;
  homeChain: BridgeConfig | undefined;
  chainId: number;
  address: string | undefined;
}

function getTokenTypeForAddress(
  address: string,
  homeChain: BridgeConfig | undefined
): "ERC721" | "ERC1155" {
  if (homeChain?.erc721Tokens.some((t) => t.address === address))
    return "ERC721";
  if (homeChain?.erc1155Tokens.some((t) => t.address === address))
    return "ERC1155";
  throw new Error("Unknown token type for address " + address);
}

export function useNFTTokens({
  signer,
  homeChain,
  chainId,
  address,
}: UseNFTTokensArgs) {
  const [nftTokens, setNFTTokens] = useState<NFTTokenDeclaration[]>([]);
  const [ownedTokenIdsByTokenAddress, setOwnedTokenIdsByTokenAddress] =
    useState<Record<string, BigNumber[]>>({});
  const [
    tokenMetadataByTokenAddressAndId,
    setTokenMetadataByTokenAddressAndId,
  ] = useState<Record<string, Record<string, ERC721Metadata>>>({});

  useEffect(() => {
    if (!homeChain || !chainId || !signer) return;

    const tokenArray: NFTTokenDeclaration[] = [];
    (homeChain.erc721Tokens || []).forEach((tokenConfig) => {
      tokenArray.push({
        ...tokenConfig,
        type: "ERC721",
        contract: ERC721Factory.connect(tokenConfig.address, signer),
      });
    });
    (homeChain.erc1155Tokens || []).forEach((tokenConfig) => {
      tokenArray.push({
        ...tokenConfig,
        type: "ERC1155",
        contract: ERC1155Factory.connect(tokenConfig.address, signer),
      });
    });
    setNFTTokens(tokenArray);
  }, [homeChain, chainId, signer]);

  const getTotalTokensOwnedForNFT = useCallback(
    async (token: ERC721 | ERC1155): Promise<number> => {
      if (!address) throw new Error("Not yet connected to wallet; no address.");

      const tokenType = getTokenTypeForAddress(token.address, homeChain);

      if (tokenType === "ERC721") {
        const response = await (token as ERC721).balanceOf(address);
        return response.toNumber();
      } else {
        const allAvailableTokenIds =
          homeChain?.erc1155Tokens.find((tc) => tc.address === token.address)
            ?.tokenIds ?? [];
        const tokenIdsAsHex = allAvailableTokenIds.map((id) => BigInt(id));
        const response = await (token as ERC1155).balanceOfBatch(
          new Array(tokenIdsAsHex.length).fill(address),
          tokenIdsAsHex
        );
        return response.filter((amount) => amount.gt(BigNumber.from(0))).length;
      }
    },
    [address, homeChain]
  );

  const updateTokenIdsOwnedForNFT = useCallback(
    async (token: ERC721 | ERC1155) => {
      if (!address) throw new Error("Not yet connected to wallet; no address.");

      const totalOwned = await getTotalTokensOwnedForNFT(token);
      let tokenIds: BigNumber[] = [];
      const tokenType = getTokenTypeForAddress(token.address, homeChain);

      if (tokenType === "ERC721") {
        for (let i = 0; i < totalOwned; i++) {
          tokenIds.push(await (token as ERC721).tokenOfOwnerByIndex(address, i));
        }
      } else {
        const allAvailableTokenIds =
          homeChain?.erc1155Tokens.find((tc) => tc.address === token.address)
            ?.tokenIds ?? [];
        const tokenIdsAsHex = allAvailableTokenIds.map((id) => BigInt(id));
        const response = await (token as ERC1155).balanceOfBatch(
          new Array(tokenIdsAsHex.length).fill(address),
          tokenIdsAsHex
        );
        response.forEach((amount, index) => {
          if (amount.gt(BigNumber.from(0))) {
            tokenIds.push(BigNumber.from(tokenIdsAsHex[index]));
          }
        });
      }

      setOwnedTokenIdsByTokenAddress((prev) => ({
        ...prev,
        [token.address]: tokenIds,
      }));
    },
    [address, homeChain, getTotalTokensOwnedForNFT]
  );

  const updateMetadataForTokenId = useCallback(
    async (token: ERC721 | ERC1155, tokenId: BigNumber | string) => {
      if (!chainbridgeConfig.ipfsGateway)
        throw new Error("IPFS gateway not configured.");

      const ipfsGateway = chainbridgeConfig.ipfsGateway;
      const tokenType = getTokenTypeForAddress(token.address, homeChain);

      let metadata: ERC721Metadata = {
        name: "⚠️ Failed to fetch metadata",
        description: "",
        image: "",
      };
      try {
        // Reads the on-chain token URI then the off-chain metadata. Both are
        // wrapped together so a contract revert (e.g. "URI query for nonexistent
        // token" when the id isn't minted on this collection) degrades to the
        // placeholder instead of surfacing as an uncaught promise rejection.
        let tokenURI =
          tokenType === "ERC721"
            ? await (token as ERC721).tokenURI(BigNumber.from(tokenId))
            : await (token as ERC1155).uri(BigNumber.from(tokenId));
        tokenURI = formatGatewayUri(tokenURI, ipfsGateway);

        const response = tokenURI ? await fetch(tokenURI) : undefined;
        const metadataJson = (await response?.json()) as ERC721Metadata;
        if (metadataJson) metadata = metadataJson;
      } catch (e) {
        console.error("Failed to fetch token metadata", e);
      }

      if (metadata.image) {
        metadata.image = formatGatewayUri(metadata.image, ipfsGateway);
      }

      setTokenMetadataByTokenAddressAndId((prev) => ({
        ...prev,
        [token.address]: {
          ...prev[token.address],
          [tokenId.toString()]: metadata,
        },
      }));
    },
    [homeChain]
  );

  return {
    nftTokens,
    getTotalTokensOwnedForNFT,
    updateTokenIdsOwnedForNFT,
    ownedTokenIdsByTokenAddress,
    updateMetadataForTokenId,
    tokenMetadataByTokenAddressAndId,
  };
}
